import React from 'react';
import Icon from 'components/AppIcon';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-warning-50 text-warning-700' },
  approved: { label: 'Approved', color: 'bg-success-50 text-success-700' },
  rejected: { label: 'Rejected', color: 'bg-error-50 text-error-700' }
};

const RateOverrideTable = ({ overrides, isAdminOrPartner, onApprove, onReject }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (overrides?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-surface rounded-2xl border border-border">
        <div className="w-16 h-16 bg-secondary-50 rounded-2xl flex items-center justify-center mb-4">
          <Icon name="Sliders" size={28} className="text-text-secondary" />
        </div>
        <p className="font-body-medium text-text-primary mb-1">No case rate overrides</p>
        <p className="text-sm text-text-secondary">Create overrides for cases requiring custom rates</p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-2xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-secondary-50">
              {isAdminOrPartner && (
                <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Lawyer</th>
              )}
              <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Case</th>
              <th className="text-right px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Standard Rate</th>
              <th className="text-right px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Override Rate</th>
              <th className="text-left px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Justification</th>
              <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Effective</th>
              <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Status</th>
              {isAdminOrPartner && (
                <th className="text-center px-4 py-3 text-xs font-body-semibold text-text-secondary uppercase tracking-wide">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {overrides?.map(override => {
              const statusCfg = STATUS_CONFIG?.[override?.approval_status] || STATUS_CONFIG?.pending;
              const rateChange = Number(override?.override_rate) - Number(override?.standard_rate);
              const rateChangePercent = ((rateChange / Number(override?.standard_rate)) * 100)?.toFixed(1);
              return (
                <tr key={override?.id} className="hover:bg-secondary-50 transition-colors">
                  {isAdminOrPartner && (
                    <td className="px-4 py-3">
                      <p className="text-sm font-body-medium text-text-primary">{override?.user_profiles?.full_name || '—'}</p>
                      <p className="text-xs text-text-secondary">{override?.user_profiles?.email}</p>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <p className="text-sm font-body-medium text-text-primary">{override?.case_name}</p>
                    <p className="text-xs text-text-secondary">{override?.case_id}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm text-text-secondary">KES {Number(override?.standard_rate)?.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div>
                      <span className="text-sm font-body-semibold text-text-primary">KES {Number(override?.override_rate)?.toLocaleString()}</span>
                      <span className={`block text-xs ${
                        rateChange > 0 ? 'text-success-600' : rateChange < 0 ? 'text-error-600' : 'text-text-secondary'
                      }`}>
                        {rateChange > 0 ? '+' : ''}{rateChangePercent}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-text-secondary line-clamp-2 max-w-xs">{override?.justification}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-text-secondary">{formatDate(override?.effective_date)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-body-medium ${statusCfg?.color}`}>
                      {statusCfg?.label}
                    </span>
                  </td>
                  {isAdminOrPartner && (
                    <td className="px-4 py-3">
                      {override?.approval_status === 'pending' ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => onApprove(override?.id, 'approved')}
                            className="flex items-center gap-1 text-xs text-success-700 hover:text-success-800 font-body-medium bg-success-50 px-2 py-1 rounded-lg"
                          >
                            <Icon name="Check" size={12} />
                            Approve
                          </button>
                          <button
                            onClick={() => onReject(override?.id)}
                            className="flex items-center gap-1 text-xs text-error-700 hover:text-error-800 font-body-medium bg-error-50 px-2 py-1 rounded-lg"
                          >
                            <Icon name="X" size={12} />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-text-secondary text-center block">
                          {formatDate(override?.approved_at)}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RateOverrideTable;
